// ============================================================
// Dashboard Service (Mock)
// ============================================================
// Always scoped by schoolId and role for multi-school context.

import { mockDashboardStats } from '@/mock/dashboardStats';
import { mockDashboardCharts } from '@/mock/dashboardCharts';
import { mockDashboardActivities } from '@/mock/dashboardActivities';
import { MOCK_DELAY } from '@/constants/app';
import { DATE_RANGES } from '@/components/filters/AnalyticsFilter';

const delay = (ms = MOCK_DELAY.MEDIUM) =>
  new Promise((resolve) => setTimeout(resolve, ms));

// Helper to scale mock data based on selected date range
const getMultiplierForDateRange = (dateRange) => {
  switch (dateRange) {
    case DATE_RANGES.TODAY:
    case DATE_RANGES.YESTERDAY:
      return 0.03; // roughly 1/30th
    case DATE_RANGES.LAST_7_DAYS:
      return 0.25; // roughly 1/4th
    case DATE_RANGES.LAST_30_DAYS:
    case DATE_RANGES.THIS_MONTH:
    case DATE_RANGES.LAST_MONTH:
      return 1.0;
    case DATE_RANGES.LAST_3_MONTHS:
      return 3.0;
    case DATE_RANGES.LAST_6_MONTHS:
      return 6.0;
    case DATE_RANGES.THIS_ACADEMIC_YEAR:
    case DATE_RANGES.LAST_ACADEMIC_YEAR:
      return 12.0;
    case DATE_RANGES.ALL_TIME:
      return 24.0;
    default:
      return 1.0;
  }
};

const applyMultiplierToStats = (stats, multiplier) => {
  if (!stats) return stats;
  const scaled = {};
  for (const [key, value] of Object.entries(stats)) {
    if (typeof value === 'number') {
      // Don't scale percentages or small integer counts that shouldn't scale linearly (like total students, maybe?)
      // For this mock, we'll scale everything except if the key includes 'total' (like totalStudents)
      if (key.toLowerCase().includes('total')) {
        scaled[key] = value; 
      } else {
        scaled[key] = Math.round(value * multiplier);
      }
    } else {
      scaled[key] = value;
    }
  }
  return scaled;
};

const applyMultiplierToCharts = (charts, multiplier) => {
  if (!charts) return charts;
  const scaled = {};
  for (const [chartName, chartData] of Object.entries(charts)) {
    scaled[chartName] = chartData.map(item => {
      const scaledItem = { ...item };
      for (const [key, value] of Object.entries(item)) {
        if (typeof value === 'number') {
          // Exception for years or identifiers
          if (key === 'year') {
            scaledItem[key] = value;
          } else {
            scaledItem[key] = Math.round(value * multiplier);
          }
        }
      }
      return scaledItem;
    });
  }
  return scaled;
};

const dashboardService = {
  /**
   * Get all dashboard data in a single payload.
   */
  getDashboardData: async ({ schoolId, role, dateRange, customStartDate, customEndDate }) => {
    await delay(MOCK_DELAY.LONG);

    if (!schoolId) throw { status: 400, message: 'School ID is required' };
    if (!role) throw { status: 400, message: 'Role is required' };

    console.log(`Fetching combined dashboard data for range: ${dateRange}`);

    const multiplier = getMultiplierForDateRange(dateRange);

    // 1. Stats
    const schoolStats = mockDashboardStats[schoolId] || Object.values(mockDashboardStats)[0];
    const rawStats = schoolStats[role] || {};
    const stats = applyMultiplierToStats(rawStats, multiplier);

    // 2. Charts
    const rawCharts = mockDashboardCharts[schoolId] || Object.values(mockDashboardCharts)[0] || {};
    const charts = applyMultiplierToCharts(rawCharts, multiplier);

    // 3. Activities
    const rawActivities = mockDashboardActivities[schoolId] || Object.values(mockDashboardActivities)[0] || [];
    // Just slice activities to simulate fewer activities for smaller date ranges
    let activities = rawActivities;
    if (multiplier < 1) {
      activities = rawActivities.slice(0, Math.max(1, Math.round(rawActivities.length * multiplier)));
    }

    return { 
      success: true, 
      data: {
        stats,
        charts,
        activities
      }
    };
  }
};

export default dashboardService;
